import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    IDLE, PENDING, LIST, ADD, EDIT, DANGER, SUCCESS,
} from "const"
import { Lifecycle } from "server/models"
import { StateType, deepCopy, hasAccess, hasProjectAccess } from "client/utils"
import {
    PRM_LIFECYCLE,
    StoreType,
    AccountSettings,
    ProjectSettings,
    LifecycleList,

    LoadAccountSettings,
    LoadProjectSettings,
    SaveAccountSettings,
    SaveProjectSettings,

    CopyDefaultLifecycles,
    LoadAllLifecycles,
    DragnDropLifecycle,
    DeleteLifecycle,
    GetMaxLifecycles,

    setSystemAccountSettings,
    setProjectProjectSettings,
    setLifecycleList,
    setLifecycleIds,
    addModal,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    PageForm,
    Feedback,
    Button,
    Loader,
    Table,
    DeleteRow,
    DragnDrop,
} from "client/components"

import AccessDenied from "client/parts/accessDenied"
import Columns from "./columns"

import ImportLifecycleList from "../importList"

const List = () => {
    const project = useSelector((state: StoreType) => state.project.projectView)
    const accountSettings = useSelector((state: StoreType) => state.system.accountSettings)
    const projectSettings = useSelector((state: StoreType) => state.project.projectSettings)
    const { defaultLifecyclesAssigned } = project.id ? projectSettings.lifecycle : accountSettings.lifecycle

    const baseRoute = useSelector((state: StoreType) => state.lifecycle.baseRoute)
    const lifecycleList = useSelector((state: StoreType) => state.lifecycle.lifecycleList)
    const lifecycleIds = useSelector((state: StoreType) => state.lifecycle.lifecycleIds)
    const reloadList = useSelector((state: StoreType) => state.lifecycle.reloadList)

    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)
    const [importState, setImportState] = useState<StateType>(IDLE)

    useEffect(() => {
        if (userData.accountId === 1) return

        if (!project.id && !accountSettings.lifecycle.defaultLifecyclesAssigned) {
            setImportState(PENDING)
            LoadAccountSettings(userData.accountId)
            .then((settings) => {
                if (!settings.lifecycle.defaultLifecyclesAssigned) {
                    CopyDefaultLifecycles(userData.accountId, null, false)
                    .then(() => {
                        const newSettings: AccountSettings = deepCopy(settings)
                        newSettings.lifecycle.defaultLifecyclesAssigned = true

                        SaveAccountSettings(userData.accountId, newSettings)
                        .then(() => {
                            dispatch(setSystemAccountSettings(newSettings))
                            setImportState(IDLE)
                        })
                    })
                }
            })
        }

        if (project.id && !projectSettings.lifecycle.defaultLifecyclesAssigned) {
            setImportState(PENDING)
            LoadProjectSettings(project.id)
            .then((settings) => {
                if (!settings.lifecycle.defaultLifecyclesAssigned) {
                    CopyDefaultLifecycles(userData.accountId, project.id, accountSettings.lifecycle.defaultLifecyclesAssigned)
                    .then(() => {
                        const newSettings: ProjectSettings = deepCopy(settings)
                        newSettings.lifecycle.defaultLifecyclesAssigned = true

                        SaveProjectSettings(project.id, newSettings)
                        .then(() => {
                            dispatch(setProjectProjectSettings(newSettings))
                            setImportState(IDLE)
                        })
                    })
                }
            })
        }
    }, [])

    const LoadData = () => {
        setLoadState(PENDING)
        LoadAllLifecycles(userData.accountId, project.id)
        .then((res) => {
            dispatch(setLifecycleList(res.lifecycleList))
            dispatch(setLifecycleIds(res.lifecycleIds))
            setLoadState(IDLE)
        })
    }

    useEffect(() => {
        if (!reloadList) return
        LoadData()
    }, [reloadList, defaultLifecyclesAssigned])

    const onDelete: DeleteRow = (lifecycle: Lifecycle, callback) => {
        DeleteLifecycle(lifecycle.id).then((removedRows) => {
            const success = removedRows === 1
            callback(success, () => {
                const lifecycleListCopy: LifecycleList = lifecycleList ? deepCopy(lifecycleList) : {}
                delete lifecycleListCopy[lifecycle.id]
                dispatch(setLifecycleList(lifecycleListCopy))
                dispatch(setLifecycleIds(lifecycleIds.filter((id) => id !== lifecycle.id)))
            })
        })
    }

    const onDragnDrop: DragnDrop = (dataIds, callback) => {
        dispatch(setLifecycleIds(dataIds))
        setLoadState(PENDING)
        DragnDropLifecycle(dataIds, lifecycleList).then((success) => {
            LoadData()
            callback(success)
        })
    }

    // max number of lifecycles
    const lifecyclesNumber = Object.keys(lifecycleList).length
    const { maxLifecycles, feedback } = GetMaxLifecycles(packageSettings, userData, lifecyclesNumber, lang)

    const maxLifecyclesReached = maxLifecycles !== null && lifecyclesNumber >= maxLifecycles
    const maxLifecyclesExceeded = maxLifecycles !== null && lifecyclesNumber > maxLifecycles

    const columns = Columns()

    const listAccess = !project.id
        ? hasAccess(PRM_LIFECYCLE, LIST)
        : hasProjectAccess(PRM_LIFECYCLE, LIST)

    const addAccess = !project.id
        ? hasAccess(PRM_LIFECYCLE, ADD)
        : hasProjectAccess(PRM_LIFECYCLE, ADD)

    if (!listAccess) return <AccessDenied />
    return (
        <Container>
            <PageHeader>
                <PageTitle>{lang.lifecycles}</PageTitle>
                {
                    addAccess && !project.id && (
                        <Button
                            variant="success"
                            disabled={maxLifecyclesReached}
                            onClick={() => dispatch(addModal({ url: `${baseRoute}/${EDIT}/0` }))}
                        >
                            {lang.add}
                        </Button>
                    )
                }
            </PageHeader>
            <PageContent>
                {
                    (loadState === PENDING || importState === PENDING)
                    && <Loader />
                }
                <Table
                    columns={columns}
                    dataList={lifecycleList}
                    dataIds={lifecycleIds}
                    rowSize="sm"
                    showPagination={false}
                    onDelete={onDelete}
                    onDragnDrop={onDragnDrop}
                    bottomContent={addAccess && userData.accountId !== 1 && (
                        <Button
                            variant="success"
                            disabled={!project.id && maxLifecyclesReached}
                            onClick={() => dispatch(addModal({
                                key: "importLifecycle",
                                content: <ImportLifecycleList />,
                            }))}
                        >
                            {lang.import}
                        </Button>
                    )}
                />
                {
                    !project.id && userData.accountId !== 1 && (
                        <PageForm>
                            <Feedback variant={maxLifecyclesExceeded ? DANGER : SUCCESS}>{feedback}</Feedback>
                        </PageForm>
                    )
                }
            </PageContent>
        </Container>
    )
}
export default List
